import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Stack } from '@mui/material';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import UIContainer from 'components/ui/UIContainer';
import WithLoader from 'components/WithLoader';
import HistoryPagination from 'components/HistoryPagination';
import { axiosInstance } from 'setup/axiosInstance';
import {
  IgnoredAtAnalyze,
  InActionAnalyzeResponse,
  InActionList,
  InActionListHistory,
  InActionSourceType,
  InActionWord,
} from 'app/types';
import { fillUrlIds, pagePaths } from 'routes/paths';
import { useNavigate } from 'react-router-dom';
import { InputMode } from './components/InputModeToggle';
import AnalysisPreview, {
  AnalysisPreviewTotals,
} from './components/AnalysisPreview';
import IAHero from './components/IAHero';
import IAInputCard from './components/IAInputCard';
import IASectionBar from './components/IASectionBar';
import IALibraryCard from './components/IALibraryCard';
import IAEmptyLibraryNote from './components/IAEmptyLibraryNote';
import IASourcePills from './components/IASourcePills';
import IALibraryComingSoon from './components/IALibraryComingSoon';

interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

const HISTORY_PER_PAGE = 6;

const getWordLabel = (word: InActionWord) =>
  word.wordKind === 'IRREGULAR_VERB' ? word.infinitive || word.word : word.word;

const sortWordsAlphabetically = (words: InActionWord[]) =>
  [...words].sort((a, b) =>
    getWordLabel(a).localeCompare(getWordLabel(b), undefined, {
      sensitivity: 'base',
    }),
  );

const emptyMissingTranslation = (words: InActionWord[]) =>
  words.map((w) => ({
    ...w,
    translation:
      w.translation?.trim().toLowerCase() === 'відсутній' ? '' : w.translation,
  }));

const emptyResponse: InActionAnalyzeResponse = {
  learnedActive: [],
  needsRepeat: [],
  notLearnedInVocabulary: [],
  notInVocabulary: [],
};

const getAllAnalyzedWords = (response: InActionAnalyzeResponse) =>
  [
    ...sortWordsAlphabetically(
      emptyMissingTranslation(response.notInVocabulary || []),
    ),
    ...sortWordsAlphabetically(response.notLearnedInVocabulary || []),
    ...sortWordsAlphabetically(response.needsRepeat || []),
    ...sortWordsAlphabetically(response.learnedActive || []),
  ].map((w, i) => ({ ...w, selected: true, sortOrder: i + 1 }));

const hasMissingTranslation = (w: InActionWord) =>
  !w.translation?.trim() ||
  w.translation.trim().toLowerCase() === 'відсутній';

type Phase = 'input' | 'preview';

const InActionPage: React.FC = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('input');
  const [mode, setMode] = useState<InputMode>('text');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewWords, setPreviewWords] = useState<InActionWord[]>([]);
  const [previewIgnoredAtAnalyze, setPreviewIgnoredAtAnalyze] = useState<
    IgnoredAtAnalyze[]
  >([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [history, setHistory] = useState<InActionListHistory[]>([]);
  const [error, setError] = useState('');
  const [historyPageNumber, setHistoryPageNumber] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(HISTORY_PER_PAGE);
  const [historyTotalElements, setHistoryTotalElements] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);

  const sourceType: InActionSourceType = file ? 'TXT_FILE' : 'TEXT';
  const isFirstTime = !historyLoading && !history.length;

  const loadHistory = async (
    page = historyPageNumber,
    size = historyPageSize || HISTORY_PER_PAGE,
  ) => {
    setHistoryLoading(true);
    try {
      const { data } = await axiosInstance.get<
        PageResponse<InActionListHistory>
      >(`/in-action/lists?page=${page}&size=${size}`);
      setHistory(data.content);
      setHistoryPageNumber(data.number);
      setHistoryPageSize(data.size);
      setHistoryTotalElements(data.totalElements);
      setHistoryTotalPages(data.totalPages);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadHistory(0, HISTORY_PER_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeHistoryPage = (page: number) => {
    loadHistory(page - 1, historyPageSize || HISTORY_PER_PAGE);
  };

  const handleModeChange = (next: InputMode) => {
    setMode(next);
    if (next === 'text' && file) setFile(null);
  };

  const analyzeFile = (selected: File) => {
    const formData = new FormData();
    formData.append('file', selected);
    return axiosInstance.post<InActionAnalyzeResponse>(
      '/in-action/analyze/file',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  };

  const analyze = async () => {
    setError('');
    setAnalyzing(true);
    try {
      const { data: analysisData } =
        mode === 'file' && file
          ? await analyzeFile(file)
          : await axiosInstance.post<InActionAnalyzeResponse>(
              '/in-action/analyze',
              { sourceType: 'TEXT', text },
            );

      const words = getAllAnalyzedWords({ ...emptyResponse, ...analysisData });

      if (!words.length) {
        setError('Не знайдено слів для збереження.');
        return;
      }

      setPreviewWords(words);
      setPreviewIgnoredAtAnalyze(analysisData.ignoredAtAnalyze || []);
      setPhase('preview');
    } catch {
      setError('Не вдалось проаналізувати текст. Спробуй ще раз.');
    } finally {
      setAnalyzing(false);
    }
  };

  const saveList = async () => {
    if (!previewWords.length) return;
    setSaving(true);
    try {
      const { data } = await axiosInstance.post<InActionList>(
        '/in-action/lists',
        {
          title,
          sourceType,
          words: previewWords,
          ignoredAtAnalyze: previewIgnoredAtAnalyze,
        },
      );
      navigate(
        fillUrlIds(pagePaths.inActionList, {
          inActionListId: String(data.id),
        }),
      );
    } catch {
      setError('Не вдалось зберегти список. Спробуй ще раз.');
    } finally {
      setSaving(false);
    }
  };

  const cancelPreview = () => {
    setPhase('input');
    setPreviewWords([]);
    setPreviewIgnoredAtAnalyze([]);
  };

  const previewTotals: AnalysisPreviewTotals = useMemo(() => {
    const learned = previewWords.filter(
      (w) => w.analysisGroup === 'LEARNED_ACTIVE',
    ).length;
    const needsRepeat = previewWords.filter(
      (w) => w.analysisGroup === 'NEEDS_REPEAT',
    ).length;
    const notLearned = previewWords.filter(
      (w) => w.analysisGroup === 'NOT_LEARNED_IN_VOCABULARY',
    ).length;
    const outOfVocab = previewWords.filter(
      (w) =>
        w.analysisGroup === 'NOT_IN_VOCABULARY' && !hasMissingTranslation(w),
    ).length;
    const needsAttention = previewWords.filter(
      (w) =>
        w.analysisGroup === 'NOT_IN_VOCABULARY' && hasMissingTranslation(w),
    ).length;
    return {
      total: previewWords.length,
      learned,
      needsRepeat,
      notLearned,
      outOfVocab,
      needsAttention,
    };
  }, [previewWords]);

  const openList = (id: number) =>
    navigate(
      fillUrlIds(pagePaths.inActionList, { inActionListId: String(id) }),
    );

  const deleteHistoryItem = async (id: number) => {
    await axiosInstance.delete(`/in-action/lists/${id}`);
    const remaining = Math.max(historyTotalElements - 1, 0);
    const lastPage = Math.max(
      Math.ceil(remaining / (historyPageSize || HISTORY_PER_PAGE)) - 1,
      0,
    );
    await loadHistory(Math.min(historyPageNumber, lastPage));
  };

  const canAnalyze =
    mode === 'file' ? !!file : text.trim().length > 0;

  return (
    <>
      <UIContainer>
        <BreadCrumbs actions={<IASourcePills />} />
      </UIContainer>
      <UIContainer>
        <Box pb={5} pt={2}>
          <Stack spacing={2.75}>
            <IAHero compact={phase === 'preview'} />

            {error && <Alert severity={'error'}>{error}</Alert>}

            {phase === 'preview' ? (
              <AnalysisPreview
                totals={previewTotals}
                title={title}
                saving={saving}
                onTitleChange={setTitle}
                onSave={saveList}
                onCancel={cancelPreview}
              />
            ) : (
              <IAInputCard
                mode={mode}
                onModeChange={handleModeChange}
                text={text}
                onTextChange={setText}
                file={file}
                onFileChange={setFile}
                analyzing={analyzing}
                onAnalyze={analyze}
                canAnalyze={canAnalyze}
              />
            )}

            <Box>
              <IASectionBar
                label={'Історія'}
                right={
                  history.length
                    ? `${historyTotalElements || history.length} записи`
                    : undefined
                }
              />
              <WithLoader loading={historyLoading && !history.length}>
                {isFirstTime ? (
                  <IAEmptyLibraryNote />
                ) : (
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(2, 1fr)',
                        },
                      }}
                    >
                      {history.map((item) => (
                        <IALibraryCard
                          key={item.id}
                          item={item}
                          onOpen={() => openList(item.id)}
                          onDelete={() => deleteHistoryItem(item.id)}
                        />
                      ))}
                    </Box>
                    <HistoryPagination
                      currentPage={historyPageNumber + 1}
                      totalPages={historyTotalPages}
                      onChange={changeHistoryPage}
                    />
                  </Stack>
                )}
              </WithLoader>
            </Box>

            <Box>
              <IASectionBar label={'Бібліотека'} />
              <IALibraryComingSoon />
            </Box>
          </Stack>
        </Box>
      </UIContainer>
    </>
  );
};

export default InActionPage;
